import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n';
import { useLocale } from '@/hooks/useLocale';

/**
 * Compact language dropdown used in Account settings, Signer page, and the
 * marketing-site nav. Takes a `variant` prop to adjust chrome (default
 * "select" for Account, "globe" for nav). Calls `onChange(code)` in addition
 * to syncing the i18n instance, so parents can persist the choice server-side.
 */
export default function LanguageSwitcher({ variant = 'select', onChange }) {
  const { i18n } = useTranslation();
  const { change } = useLocale();

  const current = i18n.language || 'en';

  const handle = (code) => {
    change(code);
    if (onChange) onChange(code);
  };

  if (variant === 'globe') {
    return (
      <div className="relative inline-block">
        <label className="sr-only">Language</label>
        <div className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors">
          <Globe size={16} />
          <select
            value={current}
            onChange={(e) => handle(e.target.value)}
            className="appearance-none bg-transparent border-none cursor-pointer focus:outline-none pr-4 text-sm"
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.native}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  return (
    <select
      value={current}
      onChange={(e) => handle(e.target.value)}
      className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent bg-white"
    >
      {SUPPORTED_LANGUAGES.map((lang) => (
        <option key={lang.code} value={lang.code}>
          {lang.native}
        </option>
      ))}
    </select>
  );
}
